import styles from './Tab.module.scss';
import AvatarWithMarketplace from '@components/AvatarWithMarketplace';
import Box from '@components/Box';
import { Tooltip } from '@components/new/Tooltip';
import DataTable from '@containers/DataTable';
import ParcelModalWindow from '@containers/ParcelModalWindow';
import { AddSellerPanel } from '@components/AddSeller';
import { FilterModel } from '@hooks/useFiltersSearchParams';
import { Filters, FilterTypes } from '@containers/Filters';
import { formatDate, createSumFromObject } from '@utils/formatValues';
import { getCurrentLanguage } from '@utils/lang';
import {
  getUniClubParcels,
  TUniClubParcel,
} from '@api/apiClient/club';
import { getErrorCode, isApiErrorWithResponse } from "@api/apiClient";
import { Modal } from '@components/Modal';
import { Row, Column } from '@components/Grid';
import { TableColumn, TableColumnType, TableRowType } from '@components/Table/TableTypes.ts';
import { Text, TextSize, TextType, TextWeight } from '@components/Text';
import TrackingNumber from "@components/TrackingNumber";
import InfoIcon from '@assets/icons/info-grey-18.svg';
import ErrorIcon from "@assets/icons/request-error.svg";
import { useQuery } from 'react-query';
import cn from 'classnames';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

const OperationsTab = ({
  isFilterOpen,
  setIsFilterOpen,
  sellersNotFound,
  onUpdate,
}: {
  isFilterOpen: boolean,
  setIsFilterOpen: (v: boolean) => void,
  sellersNotFound: boolean,
  onUpdate: () => void,
}) => {
  const [isInitialized, setIsInitialized] = useState<boolean>(false);
  const [isOpenParcelWindow, setIsOpenParcelWindow] = useState<boolean>(false);
  const [detailId, setDetailId] = useState<string>("");
  const [innerFilters, setInnerFilters] = useState<FilterModel[]>([]);
  const currentLanguage = getCurrentLanguage();
  const { t } = useTranslation();

  const { error } = useQuery({
    queryKey: ["getUniClubParcels", 1, 10],
    queryFn: () => getUniClubParcels({ page: { page: 1, pageSize: 10 }, filters: [] }),
    retry: 1,
    enabled: !sellersNotFound,
  });

  const closeParcelWindow = () => {
    setIsOpenParcelWindow(false);
  };

  const handleParcelClick = (parcelId: string) => {
    setIsOpenParcelWindow(true);
    setDetailId(parcelId);
  };

  const columns: TableColumn<TUniClubParcel>[] = useMemo(() => [
    {
      header: t("common.trackingNumber"),
      accessor: (row: TableRowType<TUniClubParcel>) => (
        <TrackingNumber
          externalId={row.data?.externalNumber}
          trackNumber={row.data?.trackNumber}
          onClick={() => handleParcelClick(row.data.id)}
          allowEqualNumbers
        />
      ),
      fixWidth: true,
      noOverflow: true,
      width: '150px',
    },
    {
      header: t("uniClub.operationsTab.columns.name"),
      accessor: (row) => {
        const firstItem = row.data.items?.[0];
        return firstItem ? (
          <a
            href={firstItem.url ?? ""}
            target="_blank"
            rel="noreferrer"
            className={styles.productLink}
          >
            {firstItem.name ?? ""}
          </a>
        ) : null;
      },
      alignment: "left",
      width: "420px",
      fixWidth: true,
      noOverflow: true,
    },
    {
      header: t('uniClub.operationsTab.columns.service'),
      accessor: (row: TableRowType<TUniClubParcel>) => (
        <Tooltip
          action={<>{row.data.serviceName}</>}
          position="top"
          text={row.data.serviceName}
          whiteSpace="pre-wrap"
        />
      ),
      width: '120px',
    },
    {
      header: t('uniClub.operationsTab.columns.store'),
      accessor: (row: TableRowType<TUniClubParcel>) => (
        <AvatarWithMarketplace
          id={row.data.sellerId}
          name={row.data.sellerName}
          marketplace={row.data.marketplaceType}
        />
      ),
      alignment: 'center',
      noOverflow: true,
    },
    {
      header: t('uniClub.operationsTab.columns.delivery'),
      accessor: (row: TableRowType<TUniClubParcel>) => (
        <Text
          size={TextSize.M}
          weight={TextWeight.MEDIUM}
        >
          {createSumFromObject(row.data.deliveryPrice)}
        </Text>
      ),
      alignment: 'right',
    },
    {
      header: t('uniClub.operationsTab.columns.level'),
      accessor: (row: TableRowType<TUniClubParcel>) => (
        <Text
          size={TextSize.M}
          weight={TextWeight.MEDIUM}
        >
          {row.data.cashbackPercent * 100} %
        </Text>
      ),
      alignment: 'left',
    },
    {
      header: (
        <div className={styles.title}>
          {t('uniClub.operationsTab.columns.cashback')}
          <Tooltip
            action={<img alt="Info" src={InfoIcon} />}
            right={8}
            text={t('uniClub.operationsTab.tooltip')}
            top={24}
            whiteSpace="pre-wrap"
          />
        </div>
      ),
      accessor: (row: TableRowType<TUniClubParcel>) => (
        <Text
          size={TextSize.S}
          className={styles.cashbackTag}
        >
          {row.data.cashback?.amount ? `+ ${row.data.cashback?.amount}`: ""}
        </Text>
      ),
      alignment: 'left',
      noOverflow: true,
    },
    {
      header: t('uniClub.operationsTab.columns.deliveryDate'),
      accessor: (row: TableRowType<TUniClubParcel>) => formatDate(row.data.deliveryDate, 'dd.MM.yy'),
      alignment: 'left',
      type: TableColumnType.TERTIARY,
    }
  ], [currentLanguage]);

  if (isApiErrorWithResponse(error)) {
    if (getErrorCode(error, "") === "external-service-error") {
      return (
        <div className={styles.placeholder}>
          <img alt="Icon" src={ErrorIcon} />
          <Text
            size={TextSize.L}
            weight={TextWeight.BOLD}
          >
            {t('uniClub.placeholder.title')}
          </Text>
          <Text
            size={TextSize.S}
            dangerouslySetInnerHTML={{
              __html: t("uniClub.placeholder.description"),
            }}
          />
        </div>
      );
    }
  }

  return (
    <div>
      <Box
        pb={5}
        className={cn(!isFilterOpen && styles.hidden)}
      >
        <Column phone={12}>
          <Row
            justify="right"
            gap={5}
          >
            <Filters
              id="uniclub-operations"
              openFilters={() => setIsFilterOpen(true)}
              items={[
                {
                  name: 'trackOrExternalNumbers',
                  type: FilterTypes.MULTI_SEARCH,
                  placeholder: t('filters.searchByTrackNumber')
                },
                {
                  name: 'sellerIds',
                  type: FilterTypes.SELLER
                },
                {
                  name: 'dateRange',
                  type: FilterTypes.DATE_RANGE,
                  placeholder: t('uniClub.operationsTab.dateFilter'),
                  fieldNames: ['startDate', 'endDate']
                }
              ]}
              onChange={(v, isReady) => {
                if (isReady) {
                  setIsInitialized(true);
                }
                setInnerFilters(v);
              }}
              values={innerFilters}
              asPanel
            />
          </Row>
        </Column>
      </Box>
      <Box pb={5}>
        <Column phone={12}>
          <Text
            size={TextSize.M}
            type={TextType.BLOCK}
            maxWidth="580px"
          >
            {t('uniClub.operationsTab.description')}
          </Text>
        </Column>
      </Box>
      {sellersNotFound ? (
        <AddSellerPanel
          onSuccess={onUpdate}
          isFirstSeller
        />
      ) : (
        <DataTable<TUniClubParcel>
          queryKey="getUniClubParcels"
          pending={!isInitialized}
          getData={getUniClubParcels}
          columns={columns}
          notFound={t('uniClub.operationsTab.noParcels')}
          filters={innerFilters}
        />
      )}
      <Modal
        isOpen={isOpenParcelWindow}
        onClose={closeParcelWindow}
        onClickOverlay={closeParcelWindow}
        width={900}
        bodyWithoutIndentations
      >
        <ParcelModalWindow
          parcelId={detailId}
        />
      </Modal>
    </div>
  );
};

export default OperationsTab;
